import React, { useEffect, useMemo, useRef, useState } from "react"
import { Form, Modal } from "react-bootstrap"
import PrimaryButton from "../subComponents/PrimaryButton"
import SecondaryButton from "../subComponents/SecondaryButton"
import { stripeDashboardUrl } from "../../utils/utils"
import "../../css/billing.css"

/**
 * Walks an admin through setting up a custom seat-tier price for one customer.
 *
 * It deliberately creates nothing. There is no API for creating a Price or writing its
 * gs_* metadata - the backend reads that metadata off Stripe and never writes it - so the
 * Stripe Dashboard is the only place this can be done. What this wizard does is remove
 * the guesswork: it works out the exact metadata for the scenario, hands it over with
 * copy buttons, states the rules that are easy to get wrong, and then verifies the result
 * so a typo shows up here rather than silently freezing the tier.
 *
 * Scenarios and rules come from gs-backend-api/docs/stripe-custom-pricing.md.
 */

type Scenario = "cappedComp" | "unlimitedDiscount" | "cappedDiscount"

interface CustomPricingWizardProps {
  show: boolean
  onClose: () => void
  productType: "SOS" | "Sensor"
  // Which Stripe account to link into, so the admin lands on the right one.
  stripeMode?: "live" | "test"
  // Deep-links straight to the product the tier Price has to be created under, instead of
  // the products list, which is where the guesswork used to start.
  stripeProductId?: string | null
  // Runs POST /adminSubscriptions/sync so the admin can confirm Stripe was set up right.
  // reprovision mirrors the same flag on the main panel's "Apply Stripe changes now":
  // false just re-reads and re-parses tiers (safe, no billing effect), true also re-runs
  // seat allocation immediately (which can charge a prorated amount right away instead of
  // waiting for the nightly run).
  onVerify: (reprovision: boolean) => Promise<void>
}

const SCENARIOS: {
  key: Scenario
  title: string
  blurb: string
  isFree: boolean
  capped: boolean
}[] = [
  {
    key: "cappedComp",
    title: "Free seats up to a limit, standard rate above",
    blurb:
      "A fixed number of seats are free (a pilot or a promo). Everything above that bills at the standard rate. This is the only scenario that can be given an expiry date.",
    isFree: true,
    capped: true,
  },
  {
    key: "unlimitedDiscount",
    title: "One discounted rate on every seat",
    blurb:
      "A negotiated rate that applies to every seat the customer has, no matter how many, for as long as it is in place.",
    isFree: false,
    capped: false,
  },
  {
    key: "cappedDiscount",
    title: "Discounted rate up to a limit, standard rate above",
    blurb:
      "An early-adopter or negotiated rate on the first N seats, with the standard rate above that.",
    isFree: false,
    capped: true,
  },
]

const CopyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clears on every unmount, not just before a new timer starts, so a click followed by
  // closing the wizard within 1.5s can't fire setState on an unmounted CopyRow.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard is unavailable outside a secure context. The value is on screen and
      // selectable, so there is nothing to recover from here.
    }
  }

  return (
    <tr>
      <td style={{ whiteSpace: "nowrap" }}>{label}</td>
      <td>{value}</td>
      <td>
        <button type='button' className='billing-copy-btn' onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </td>
    </tr>
  )
}

const CustomPricingWizard: React.FC<CustomPricingWizardProps> = ({
  show,
  onClose,
  productType,
  stripeMode,
  stripeProductId,
  onVerify,
}) => {
  const [step, setStep] = useState(1)
  const [scenario, setScenario] = useState<Scenario>("cappedComp")
  const [seatCap, setSeatCap] = useState("2")
  const [rate, setRate] = useState("")
  const [expiresAt, setExpiresAt] = useState("")
  const [label, setLabel] = useState("")
  const [isVerifying, setIsVerifying] = useState(false)
  const [verifyingReprovision, setVerifyingReprovision] = useState(false)
  // "Verify and apply now" can charge a prorated amount immediately, same as the main
  // panel's "Apply Stripe changes now" - which is confirm-gated. This one-click button
  // was not, so it gets the same gate here.
  const [confirmApply, setConfirmApply] = useState(false)

  const selected = SCENARIOS.find((s) => s.key === scenario)!

  const metadata = useMemo(() => {
    // gs_seat_cap must be a positive whole number - the number input's min={1} only
    // stops the spinner buttons, not typing "0" or a negative value directly, and
    // either would pass straight through into the metadata an admin copies into
    // Stripe. Clamped here rather than left to the backend, which would just freeze
    // the tier with a "gs_seat_cap must be a positive integer" problem the admin then
    // has to come back here to puzzle out.
    const trimmedSeatCap = seatCap.trim()
    const parsedSeatCap = trimmedSeatCap === "" ? 2 : parseInt(trimmedSeatCap, 10)
    const seatCapValue = String(
      Number.isFinite(parsedSeatCap) && parsedSeatCap >= 1 ? parsedSeatCap : 1,
    )
    const rows: { label: string; value: string }[] = [
      { label: "gs_type", value: productType },
      { label: "gs_seat_cap", value: selected.capped ? seatCapValue : "unlimited" },
    ]
    // gs_expires_at is only legal on a $0 price. A nonzero tier that expired would bill
    // the discounted line for the new period and then prorate the same seats onto the
    // standard price, so the backend freezes such a tier rather than accept it.
    if (selected.isFree && expiresAt.trim() !== "") {
      rows.push({ label: "gs_expires_at", value: expiresAt.trim() })
    }
    if (label.trim() !== "") {
      rows.push({ label: "gs_label", value: label.trim() })
    }
    return rows
  }, [productType, selected, seatCap, expiresAt, label])

  const priceAmount = selected.isFree ? "0.00" : rate.trim() || "(the negotiated amount)"

  const reset = () => {
    setStep(1)
    setScenario("cappedComp")
    setSeatCap("2")
    setRate("")
    setExpiresAt("")
    setLabel("")
    setConfirmApply(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  const verify = async (reprovision: boolean) => {
    setIsVerifying(true)
    setVerifyingReprovision(reprovision)
    try {
      await onVerify(reprovision)
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <Modal className='comman-modal' show={show} onHide={close} centered size='lg'>
      <Modal.Header closeButton className='comman-modal-header'>
        <Modal.Title className='comman-modal-title'>
          Set up custom pricing ({productType}) - step {step} of 3
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='comman-modal-body'>
        {step === 1 && (
          <>
            <p>
              Pick the arrangement this customer has been given. Each one is a separate Stripe
              Price attached to their subscription, and the customer's cheapest seats always fill
              first.
            </p>
            {SCENARIOS.map((s) => (
              <label
                key={s.key}
                className={`billing-scenario ${scenario === s.key ? "is-selected" : ""}`}
              >
                <input
                  type='radio'
                  name='scenario'
                  checked={scenario === s.key}
                  onChange={() => setScenario(s.key)}
                />
                <span>
                  <strong>{s.title}</strong>
                  <span className='billing-scenario-blurb'>{s.blurb}</span>
                </span>
              </label>
            ))}
          </>
        )}

        {step === 2 && (
          <>
            <p>{selected.blurb}</p>
            <div className='row'>
              {selected.capped && (
                <div className='col-12 col-sm-6'>
                  <div className='form-group login-input-section'>
                    <label htmlFor='seatCap'>
                      How many seats at this {selected.isFree ? "free" : "discounted"} rate?
                    </label>
                    <Form.Control
                      id='seatCap'
                      type='number'
                      min={1}
                      value={seatCap}
                      onChange={(e) => setSeatCap(e.target.value)}
                      placeholder='e.g. 2'
                    />
                  </div>
                </div>
              )}
              {!selected.isFree && (
                <div className='col-12 col-sm-6'>
                  <div className='form-group login-input-section'>
                    <label htmlFor='rate'>Annual rate per seat (USD)</label>
                    <Form.Control
                      id='rate'
                      type='number'
                      min={0}
                      step='0.01'
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      placeholder='e.g. 200.00'
                    />
                  </div>
                </div>
              )}
              {selected.isFree && (
                <div className='col-12 col-sm-6'>
                  <div className='form-group login-input-section'>
                    <label htmlFor='expiresAt'>Expiry date (optional)</label>
                    <Form.Control
                      id='expiresAt'
                      type='date'
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                    />
                    <span className='billing-note'>
                      Leave blank for no expiry. Only free tiers can expire.
                    </span>
                  </div>
                </div>
              )}
              <div className='col-12 col-sm-6'>
                <div className='form-group login-input-section'>
                  <label htmlFor='label'>Label (optional)</label>
                  <Form.Control
                    id='label'
                    type='text'
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder='e.g. Pilot program'
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {step === 3 && confirmApply && (
          <>
            <p>
              Re-runs seat allocation immediately instead of waiting for the nightly run.{" "}
              <strong>
                If this adds seats to this or any other tier, the customer is charged a prorated
                amount straight away.
              </strong>{" "}
              Removing seats never produces a refund or credit.
            </p>
            <div className='d-flex gap-3 justify-content-center mt-3'>
              <SecondaryButton
                type='button'
                label='Back'
                onClick={() => setConfirmApply(false)}
                disabled={isVerifying}
              />
              <PrimaryButton
                type='button'
                label={isVerifying ? "Applying..." : "Yes, apply now"}
                onClick={() => verify(true)}
                disabled={isVerifying}
              />
            </div>
          </>
        )}

        {step === 3 && !confirmApply && (
          <>
            <p>
              Create this Price in the Stripe Dashboard under the existing product, then attach it
              to the customer's subscription as a new item.
              {stripeMode && (
                <>
                  {" "}
                  These links open your <strong>{stripeMode} mode</strong> Stripe account, the one
                  this environment is configured against.
                </>
              )}
            </p>

            <h4 className='owner-details-title'>The Price</h4>
            <ul className='billing-rules'>
              <li>
                Recurring, <strong>yearly</strong>, flat per-unit
              </li>
              <li>
                Amount: <strong>${priceAmount}</strong> per seat
              </li>
            </ul>

            <h4 className='owner-details-title'>Metadata to set on that Price</h4>
            <div style={{ overflowX: "auto" }}>
              <table className='billing-meta-table'>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {metadata.map((row) => (
                    <CopyRow key={row.label} label={row.label} value={row.value} />
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className='owner-details-title mt-3'>Before you go</h4>
            <ul className='billing-rules'>
              <li>
                <strong>
                  Create the Price under the {productType} product specifically
                </strong>{" "}
                - the button below opens the right one. Each product line carries its own tax
                code (SOS is software, Sensor is alarm monitoring), and they are taxed
                differently. A Price created under the wrong product still bills the customer
                correctly but taxes them as the wrong kind of product, so this system refuses to
                use it and freezes the tier instead. A Price cannot be moved between products
                afterwards, only recreated.
              </li>
              <li>
                Use a flat per-seat price. Stripe's own graduated or volume tiering, and
                "customer chooses the price", cannot be read by this system and will freeze the
                tier.
              </li>
              {selected.capped && (
                <li>
                  Leave the standard annual Price attached to the subscription as well, so seats
                  above the cap have somewhere to go. Without it those seats are reported as
                  unallocated and nothing is billed for them.
                </li>
              )}
              {!selected.isFree && (
                <li>
                  A Stripe price amount cannot be edited later. Changing this rate in future means
                  creating a new Price and swapping the subscription item over to it.
                </li>
              )}
              <li>
                If Stripe offers to prorate and invoice when you attach the item, either answer is
                safe - "Verify and apply now" below covers the decline case.
              </li>
            </ul>

            <div className='billing-wizard-actions mt-3'>
              <a
                className='primaryButton com-button'
                href={stripeDashboardUrl(
                  stripeMode,
                  stripeProductId ? `products/${stripeProductId}` : "products",
                )}
                target='_blank'
                rel='noreferrer'
              >
                {stripeProductId
                  ? `Open the ${productType} product in Stripe`
                  : "Open Stripe products"}
              </a>
              <SecondaryButton
                type='button'
                label={isVerifying && !verifyingReprovision ? "Checking..." : "Verify only"}
                onClick={() => verify(false)}
                disabled={isVerifying}
              />
              <PrimaryButton
                type='button'
                label='Verify and apply now'
                onClick={() => setConfirmApply(true)}
                disabled={isVerifying}
              />
            </div>
            <p className='billing-note mt-2'>
              Both re-read the subscription from Stripe and show how each Price was understood, so
              a mistyped metadata key shows up as a problem on the tier instead of going unnoticed.{" "}
              <strong>Verify only</strong> stops there: nothing else changes, and seat allocation
              waits for the nightly run. <strong>Verify and apply now</strong> also re-runs seat
              allocation immediately - use it if you declined Stripe's prorate-and-invoice prompt
              above, or just want this tier live right away, and asks for confirmation first since
              it can charge a prorated amount straight away.
            </p>
          </>
        )}

        {!(step === 3 && confirmApply) && (
          <div className='d-flex gap-3 justify-content-between mt-4'>
            <SecondaryButton
              type='button'
              label={step === 1 ? "Cancel" : "Back"}
              className='editFormButton'
              onClick={() => (step === 1 ? close() : setStep(step - 1))}
            />
            {step < 3 && (
              <PrimaryButton
                type='button'
                label='Next'
                className='editFormButton'
                onClick={() => setStep(step + 1)}
              />
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default CustomPricingWizard
